import { inject } from "@angular/core";
import { CanMatchFn, Router } from "@angular/router";
import { PLATFORM_ADAPTER } from "../../core/platform/platform.adapter";
import { ClientTokenService } from "./client-token.service";
import { WebSessionBootstrapService } from "./web-session-bootstrap.service";

/**
 * canMatch guard for browser builds: protected routes won't even start
 * loading without a non-expired clientToken AND a completed session
 * bootstrap. Short-circuits to `true` on native — mobile auth runs through
 * AuthService, not ClientTokenService.
 *
 * Using canMatch (vs canActivate) means lazy-loaded modules aren't
 * fetched at all for unauthenticated users.
 *
 * **Bootstrap-readiness gate** — awaits
 * `WebSessionBootstrapService.readyForToken(token)` so the destination
 * page mounts on a populated classCom rather than racing the async
 * bootstrap that AppComponent's tokenObs subscriber kicked off. On
 * `unauthorized` the bootstrap has already navigated to /login; we
 * return `false` so this route activation gives up gracefully.
 */
export const webAuthGuard: CanMatchFn = async (_route, segments) => {
  const platform = inject(PLATFORM_ADAPTER);
  if (platform.isNative) return true;

  const clientToken = inject(ClientTokenService);
  const token = clientToken.getTokenSync();
  if (!token) {
    const router = inject(Router);
    const target = "/" + segments.map((s) => s.path).join("/");
    return router.createUrlTree(["/login"], {
      queryParams: target && target !== "/" ? { redirect: target } : undefined,
    });
  }

  const webBootstrap = inject(WebSessionBootstrapService);
  const result = await webBootstrap.readyForToken(token);
  if (result.status === "unauthorized") {
    // Bootstrap routed to /login via WebAuthRedirector, but that navigateByUrl
    // is fire-and-forget and races our return. Returning bare `false` from a
    // CanMatchFn aborts the route match without redirecting — under contention
    // the user lands in an unrouted state. Return a UrlTree so Angular owns
    // the redirect deterministically; redundant with the redirector's nav but
    // safe (Router collapses identical navigations).
    const router = inject(Router);
    return router.createUrlTree(["/login"]);
  }
  // Re-check the token after the await — if it rotated while we were waiting,
  // the BootstrapResult belongs to the prior identity. Redirect to /login
  // rather than returning bare false; the new token's guard run will pick up
  // the current state on the next emission, and login → autoredirect handles
  // the case where the new token is already authenticated.
  if (clientToken.getTokenSync() !== token) {
    const router = inject(Router);
    return router.createUrlTree(["/login"]);
  }
  if (result.status === "error" || result.status === "stale") {
    // Transient bootstrap failure (server hiccup, network blip) — the
    // user is authenticated, but classCom isn't populated. Bouncing to
    // /login on every transient miss would hide real connectivity issues
    // and lose the URL the user was navigating to. Render the destination
    // and rely on the page to surface a "couldn't load" state rather than
    // mis-rendering against empty identity. The bootstrap will retry on
    // the next tokenObs emission.
    console.warn(
      `[webAuthGuard] bootstrap status=${result.status} for current token — ` +
        `route activating with potentially-empty classCom; destination must ` +
        `surface a useful empty state.`
    );
  }
  // 'applied' / 'empty' / 'error' / 'stale' → render the route. 'empty'
  // means the registration has no active duty; the page must render an
  // empty state. 'error' / 'stale' get the warn above.
  return true;
};
